import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Grid, Box, Container, Typography } from '@material-ui/core';
import i18n from 'i18n-js';
import moment from 'moment';
import { usePrevious } from 'hooks';
import { forceTimezone } from 'utils';
import useStyles from './Rulesets.styled';
import { EditableTable } from 'components/UI';
import { KeyboardDatePicker } from '@material-ui/pickers';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { BusinessHoursActions } from 'state/modules/businessHours';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';

const BusinessHoursRulesets = ({ currentGroup }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [columns, setColumns] = React.useState([]);
  const prevCurrentGroup = usePrevious(currentGroup?.id);
  const { bh_times } = useSelector((state) => state.businessHours);
  const { localeFormat } = useSelector((state) => state.organizations);
  const rulesets = useSelector((state) => state.vehiclesGroups.bhTimes);

  const handleRuleset = (payload, ruleset, resolve, reject) => {
    dispatch(
      VehiclesGroupsActions.fetchBHRulesetToVehiclesGroups(
        currentGroup?.id,
        ruleset,
        payload,
        resolve,
        reject
      )
    );
  };

  const deleteRuleset = (ruleset, resolve, reject) => {
    dispatch(
      VehiclesGroupsActions.fetchDeleteBHRulesetFromVehiclesGroups(
        currentGroup?.id,
        ruleset,
        resolve,
        reject
      )
    );
  };

  const parseTableRow = () =>
    rulesets?.map((val) => {
      return {
        id: val?.id,
        bh_rule_set_id: val?.business_hours_rules_set.id,
        start_date: val?.start_time
          ? forceTimezone(moment(val?.start_time)).format(localeFormat)
          : '',
        end_date: val?.end_time ? forceTimezone(moment(val?.end_time)).format(localeFormat) : '',
      };
    });

  useEffect(() => {
    if (currentGroup?.id && prevCurrentGroup !== currentGroup?.id) {
      dispatch(VehiclesGroupsActions.fetchVehicleGroupsBHTimes(currentGroup?.id));
    }
  }, [currentGroup, prevCurrentGroup, dispatch]);

  useEffect(() => {
    dispatch(BusinessHoursActions.fetchBusinessHours({ page: 1, limit: 100, archived: false }));
  }, [dispatch]);

  useDeepCompareEffect(() => {
    if (bh_times?.length > 0) {
      const parsedRuleName = bh_times.reduce((obj, val) => {
        obj[val.id] = val.name;
        return obj;
      }, {});

      setColumns([
        {
          title: i18n.t('common.ruleset_name'),
          field: 'bh_rule_set_id',
          lookup: parsedRuleName,
          dataCy: 'ruleSelect',
        },
        {
          title: i18n.t('common.start_date'),
          field: 'start_date',
          editable: 'onAdd',
          editComponent: (props) => {
            return (
              <KeyboardDatePicker
                id="date-start"
                label="Start date"
                format="MM/DD/YYYY"
                className="material-datepicker"
                minDate={new Date()}
                value={props.value ? moment(new Date(props.value)) : null}
                onChange={props.onChange}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            );
          },
        },
        {
          title: i18n.t('common.end_date'),
          field: 'end_date',
          editable: 'onAdd',
          editComponent: (props) => {
            return (
              <KeyboardDatePicker
                id="date-end"
                label="End date"
                format="MM/DD/YYYY"
                onChange={props.onChange}
                className="material-datepicker"
                KeyboardButtonProps={{ 'aria-label': 'change date' }}
                value={props.value ? moment(new Date(props.value)) : null}
                minDate={moment(props?.rowData?.start_date).toDate() || new Date()}
              />
            );
          },
        },
      ]);
    }
  }, [bh_times]);

  return (
    <Box className={classes.container}>
      <Container className="h-100">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid item xs={12}>
            <Typography gutterBottom component="div">
              <Box fontWeight="fontWeightMedium" fontSize={14} my={2}>
                {i18n.t('vehicle_groups.business_hours.bh_rulesets')}
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {bh_times?.length > 0 ? (
              <EditableTable
                addButtonProps={{
                  variant: 'outlined',
                  position: 'flex-end',
                  dataCy: 'add-new-rrs',
                  label: i18n.t('pricing_rules.add_new_rule'),
                }}
                requiredFields={['bh_rule_set_id']}
                data={parseTableRow() || []}
                columns={columns || []}
                onRowAdd={(values, resolve, reject) => {
                  let payload = {
                    bh_rule_set_id: parseInt(values.bh_rule_set_id),
                  };

                  if (values.start_date) {
                    if (moment(values.start_date).isSame(moment(), 'day')) {
                      payload.start_date = undefined;
                    } else {
                      payload.start_date = moment(values.start_date).format('YYYY-MM-DD');
                    }
                  }

                  if (values.end_date) {
                    payload.end_date = moment(values.end_date).format('YYYY-MM-DD');
                  }
                  handleRuleset(payload, null, resolve, reject);
                }}
                onDelete={(values, resolve, reject) => {
                  deleteRuleset(values.id, resolve, reject);
                }}
                onEdit={(values, resolve, reject) => {
                  let payload = {
                    new_bh_rule_set_id: parseInt(values.bh_rule_set_id),
                  };

                  if (values.start_date) {
                    if (moment(values.start_date).isSame(moment(), 'day')) {
                      payload.start_date = undefined;
                    } else {
                      payload.start_date = moment(values.start_date).format('YYYY-MM-DD');
                    }
                  }

                  if (values.end_date) {
                    payload.end_date = moment(values.end_date).format('YYYY-MM-DD');
                  }
                  handleRuleset(payload, values.id, resolve, reject);
                }}
              />
            ) : (
              <Box mt={2} display="flex" justifyContent="center">
                <Typography color="textPrimary" style={{ lineHeight: 1 }}>
                  <Box fontSize={14} component="span" lineHeight={1}>
                    {i18n.t('vehicle_groups.business_hours.empty_bhr')}
                  </Box>
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default BusinessHoursRulesets;
